import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Area } from 'react-easy-crop/types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormCheck, FormGroup } from 'react-bootstrap';
import { useSetRecoilState } from 'recoil';

import {
    Modal,
    ModalBody,
    ModalButton,
    ModalFooter,
    ModalSimpleHeader
} from 'components/shared/Modal';
import { ImageUploader } from 'components/modals/UpdateProfileImageModal/ImageUploader';
import { ImagePosition } from 'components/modals/UpdateProfileImageModal/ImagePosition';
import { getCroppedImg } from 'shared/canvas-utils/canvas-utils';
import { ProfileImageUpdatedModal } from 'components/modals/ProfileImageUpdatedModal/ProfileImageUpdatedModal';
import { individualProfilePicture } from 'state/individual/individual-state';

import './styles.scss';

interface IUpdateProfileImageForm {
    genuineImageConfirmation: true;
}

const updateProfileImageFormSchema = z.object({
    genuineImageConfirmation: z.literal(true),
});

interface IUpdateProfileImageModalProps {
    onClose: () => void;
    show: boolean;
    readOnly?: boolean;
    loadRecaptcha?: () => void;
}

export const UpdateProfileImageModal = ({onClose, show, loadRecaptcha}: IUpdateProfileImageModalProps) => {
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [image, setImage] = React.useState<string | undefined>();
    const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null);
    const [rotation, setRotation] = React.useState<number>(0);
    const [showProfileUpdated, setShowProfileUpdated] = React.useState<boolean>();

    const setProfilePicture = useSetRecoilState(individualProfilePicture);

    const {t} = useTranslation();

    const {handleSubmit: handleSubmitImage, register: registerImage, formState: { errors: errorsImage }} = useForm<IUpdateProfileImageForm>({
        resolver: zodResolver(updateProfileImageFormSchema)
    });

    /**
     * Applies the selected crop to the image and gets a new image type (base64 string) then sends that to the Service
     * Layer
     */
    const sendCroppedImage = React.useCallback(async () => {
        if (image && croppedAreaPixels) {
            try {
                const croppedImage = await getCroppedImg(
                    image,
                    croppedAreaPixels,
                    rotation
                );
                if (croppedImage) {
                    // Request can be sent from here if desired.
                    setProfilePicture(croppedImage);
                    return true;
                }
            } catch (e) {
                console.error(e);
            }
            return false;
        }
    }, [image, croppedAreaPixels, rotation, setProfilePicture]);

    const onSubmit = () => {
        setIsSubmitting(true);
        loadRecaptcha?.();
        // submit here
        sendCroppedImage().then((resolved) => {
            setIsSubmitting(false);
            if (resolved) {
                setShowProfileUpdated(true);
            }
        });
    };

    if (showProfileUpdated) {
        return (
            <ProfileImageUpdatedModal handleClose={onClose} show={true}/>
        );
    }

    return (
        <Modal show={show} onHide={onClose} isSubmitting={isSubmitting}>
            <Form onSubmit={handleSubmitImage(onSubmit)}>
                <FormGroup className="mb-0">
                    <ModalSimpleHeader>
                        {t('updateProfileImageModal.addProfileImage', 'Add Profile Image')}
                    </ModalSimpleHeader>
                    <ModalBody>
                        {!image ? (
                            <ImageUploader setImage={setImage}/>
                        ) : (
                            <>
                                <div className="UpdateProfileImage__title">
                                    {t('UpdateProfileImage.imagePosition', 'Image Position')}
                                </div>
                                <div className="UpdateProfileImage__subtitle mb-3">
                                    <Trans i18nKey="UpdateProfileImage.pleaseResizeImage">
                                        Zoom, drag or rotate your image so that it fits neatly in the area
                                        provided. The profile image must be a clear, high quality passport style image
                                        of your face.
                                    </Trans>
                                </div>
                                <ImagePosition
                                    image={image}
                                    setCroppedAreaPixels={setCroppedAreaPixels}
                                    setRotation={setRotation}
                                    rotation={rotation}
                                />

                                <div className="UpdateProfileImage__title">
                                    {t('common.confirmation', 'Confirmation')}
                                </div>
                                <div className="UpdateProfileImage__subtitle mb-3">
                                    <Trans i18nKey="UpdateProfileImage.confirmImageIsGenuine">
                                        Please confirm that this is a genuine image you.
                                    </Trans>
                                </div>
                                <Form.Check custom className="mb-4">
                                    <FormCheck.Input
                                        type="checkbox"
                                        id="UpdateProfileImage__genuineImageConfirmation"
                                        name="genuineImageConfirmation"
                                        ref={registerImage()}
                                        isInvalid={!!errorsImage?.genuineImageConfirmation}
                                    />
                                    <FormCheck.Label htmlFor="UpdateProfileImage__genuineImageConfirmation">
                                        <Trans i18nKey="UpdateProfileImage.confirmationCheckboxLabel">
                                            I declare that this is a genuine image of me.
                                        </Trans>
                                    </FormCheck.Label>
                                    {(errorsImage && errorsImage.genuineImageConfirmation) && (
                                        <Form.Control.Feedback type="invalid">
                                            <Trans i18nKey="UpdateProfileImage.errors.confirmation">
                                                Please ensure this is a genuine image of the worker.
                                            </Trans>
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Check>
                                <div className="UpdateProfileImage__disclaimer bg-secondary">
                                    <Trans i18nKey="UpdateProfileImage.disclaimer">
                                        The submitted profile image will be reviewed to ensure that it meets the Profile
                                        Image Guidelines. The image will be immediately applied to your profile. You
                                        will only be notified if the profile image is reviewed and rejected.
                                    </Trans>
                                </div>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter cancelButton={t('actions.cancel', 'Cancel')}>
                        {image && (
                            <ModalButton
                                className="btn-xl border"
                                variant="primary"
                                type="submit"
                            >
                                {t('actions.addProfileImage', 'Add Profile Image')}
                            </ModalButton>
                        )}
                    </ModalFooter>
                </FormGroup>
            </Form>
        </Modal>
    );
};
